import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBooking } from '../hooks/useBooking';
interface DatePickerProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  type: 'checkIn' | 'checkOut';
}
export function DatePicker({
  isOpen,
  onOpen,
  onClose,
  type
}: DatePickerProps) {
  const {
    checkIn,
    checkOut,
    setDates
  } = useBooking();
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  const formatDate = (date: Date | null) => {
    if (!date) return 'Add dates';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  const handleDateClick = (day: number) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    selectedDate.setHours(0, 0, 0, 0);
    if (type === 'checkIn') {
      if (checkOut && selectedDate >= checkOut) {
        setDates(selectedDate, null);
      } else {
        setDates(selectedDate, checkOut);
      }
      if (!checkOut) {
        // Keep open to select checkout, but we'll let parent handle focus shift if needed
      } else {
        onClose();
      }
    } else {
      if (checkIn && selectedDate <= checkIn) {
        setDates(selectedDate, null);
      } else {
        setDates(checkIn, selectedDate);
        onClose();
      }
    }
  };
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = [];
    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    // Weekday headers
    weekDays.forEach(day => {
      days.push(<div key={`header-${day}`} className="text-center text-xs font-semibold text-gray-400 py-2" data-id="element-20">
          {day}
        </div>);
    });
    // Empty cells for days before start of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2" data-id="element-21"></div>);
    }
    // Days of the month
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);
      const isPast = date < today;
      const isCheckIn = checkIn && date.getTime() === checkIn.getTime();
      const isCheckOut = checkOut && date.getTime() === checkOut.getTime();
      const isBetween = checkIn && checkOut && date > checkIn && date < checkOut;
      let className = 'w-8 h-8 mx-auto flex items-center justify-center rounded-full text-sm transition-colors ';
      let wrapperClass = 'py-1 ';
      if (isPast) {
        className += 'text-gray-300 cursor-not-allowed';
      } else if (isCheckIn || isCheckOut) {
        className += 'bg-luxury-blue text-white font-semibold shadow-md';
        if (isCheckIn && checkOut) wrapperClass += 'bg-luxury-blue/10 rounded-l-full';
        if (isCheckOut && checkIn) wrapperClass += 'bg-luxury-blue/10 rounded-r-full';
      } else if (isBetween) {
        className += 'text-luxury-slate font-medium';
        wrapperClass += 'bg-luxury-blue/10';
      } else {
        className += 'text-gray-700 hover:bg-gray-100 cursor-pointer font-medium';
      }
      days.push(<div key={`day-${d}`} className={wrapperClass} data-id="element-22">
          <button disabled={isPast} onClick={() => handleDateClick(d)} className={className} data-id="element-23">
            {d}
          </button>
        </div>);
    }
    return days;
  };
  return <div className="relative flex-1 min-w-0" ref={containerRef} data-id="element-24">
      <div className="p-3 md:px-5 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer group h-full flex flex-col justify-center" onClick={onOpen} data-id="element-25">
        <label className="block text-xs font-semibold text-gray-800 mb-1 uppercase tracking-wider cursor-pointer" data-id="element-26">
          {type === 'checkIn' ? 'Check in' : 'Check out'}
        </label>
        <div className="flex items-center text-gray-500 group-hover:text-gray-900 min-w-0" data-id="element-27">
          <CalendarIcon className="w-4 h-4 mr-2 text-luxury-blue shrink-0" data-id="element-28" />
          <span className={`text-sm font-medium truncate ${!checkIn && type === 'checkIn' || !checkOut && type === 'checkOut' ? 'text-gray-400 italic' : 'text-gray-900'}`} data-id="element-29">
            {type === 'checkIn' ? formatDate(checkIn) : formatDate(checkOut)}
          </span>
        </div>
      </div>

      <AnimatePresence data-id="element-30">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="absolute top-full left-0 md:-left-1/2 mt-4 w-72 md:w-80 bg-white rounded-2xl shadow-float border border-gray-100 p-4 z-50" data-id="element-31">
            <div className="flex items-center justify-between mb-4" data-id="element-32">
              <button onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-600" data-id="element-33">
                <ChevronLeft className="w-5 h-5" data-id="element-34" />
              </button>
              <div className="font-semibold text-luxury-slate" data-id="element-35">
                {currentMonth.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric'
            })}
              </div>
              <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-600" data-id="element-36">
                <ChevronRight className="w-5 h-5" data-id="element-37" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-y-1" data-id="element-38">{renderCalendar()}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}